import React, { useMemo } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import cn from 'classnames';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { toSvgColor } from 'apps/sigma2-ui/src/components/svg/types';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { MetricChangeProps } from 'libs/end-to-end/src/progress';
import { MetricChangeSectionSerializableProps } from 'libs/end-to-end/src/progress';
import { guardExhaustive } from 'libs/end-to-end/src/generic';

interface MetricChangePropsFormatted extends MetricChangeProps {
  label: string;
  emphasized: boolean;
  metricDefinition: MyMetricDefinition;
  focusArea: FocusArea;
}

const JumpArrowIcon: React.FC<SvgProps> = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="14"
      viewBox="0 0 11 14"
      fill="none"
      {...(props.svg ?? {})}
    >
      <path
        d="M5.30762 14L5.30762 2M5.30762 2L9.61525 6.30762M5.30762 2L1 6.30762"
        stroke="#F07B54"
        strokeWidth="1.5"
      />
    </svg>
  );
};

const MetricChangeDirectionIcon: React.FC<SvgProps> = (props) => {
  const fillColor = toSvgColor('currentColor', props);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="9"
      height="6"
      viewBox="0 0 9 6"
      fill="none"
      {...(props.svg ?? {})}
    >
      <path d="M0 6L4.5 0L9 6H0Z" fill={fillColor} />
    </svg>
  );
};

const MetricChange = ({
  label,
  change,
  emphasized,
  startDate,
  endDate,
  confidence,
  metricDefinition,
  focusArea,
  duration,
  direction,
}: MetricChangePropsFormatted) => {
  const language = useLanguage();
  const units = useMemo(
    () =>
      determineUnitOfMetric({
        knownMaximum: null,
        isMetricBoolean: metricDefinition.isMetricBoolean,
        metricFocusArea: focusArea,
        definitionUnit: metricDefinition.unit,
      }),
    [focusArea, metricDefinition.isMetricBoolean, metricDefinition.unit],
  );

  const directionIconColorClass = useMemo(() => {
    const framing = metricDefinition.calibrationFraming;

    const goingUp = direction === 'up';

    switch (framing) {
      case 'Positive':
        return goingUp ? 'text-teal-400' : 'text-red-400';
      case 'Negative':
        return goingUp ? 'text-red-400' : 'text-teal-400';
      default:
        throw guardExhaustive(framing);
    }
  }, [direction, metricDefinition.calibrationFraming]);

  return (
    <Spacing
      direction="vertical"
      size="gap-1"
      className={cn('py-4', {
        'bg-[#FFFBF5]': emphasized,
        'pl-3': emphasized,
        'pr-5': emphasized,
      })}
    >
      <div className="flex items-center gap-2">
        {emphasized && (
          <JumpArrowIcon
            svg={{
              className: cn({
                'rotate-180': direction === 'down',
              }),
            }}
          />
        )}
        <S2Typography
          translatedContent={label}
          textColorVariant={'gray-900'}
          variant={'headline-7-alt'}
          dangerouslyOverrideClassNameFn={(cn) =>
            `${cn.replace('text-gray-900', '')} ${emphasized ? ' text-[#F07B54]' : ''}`
          }
        />
      </div>
      <div
        className={cn({
          'pl-4': emphasized,
        })}
      >
        <Spacing direction="horizontal" size="gap-0.5" className="items-center">
          <S2Typography
            translatedContent={formatNumberForDisplayInUnits({
              value: change,
              isPercentageAlreadyMultipliedBy100: false,
              units,
            })}
            textColorVariant={'gray-900'}
            variant={'headline-4-alt'}
            dangerouslyOverrideClassNameFn={(cn) => `${cn} font-light`}
          />
          <MetricChangeDirectionIcon
            wantsCurrentColor
            svg={{
              className: cn(directionIconColorClass, {
                'rotate-180': direction === 'down',
              }),
            }}
          />
        </Spacing>
        <div>
          <S2Typography
            translatedContent={language.dateRange({
              start: startDate,
              end: endDate,
            })}
            textColorVariant={'gray-900'}
            variant={'body-6'}
          />
          <S2Typography
            translatedContent={language.dateDuration({
              duration,
            })}
            textColorVariant={'gray-600'}
            variant={'body-7'}
          />
        </div>
      </div>
      <LightTooltip
        title={
          <S2Typography
            as={'span'}
            variant={'body-7'}
            textColorVariant="gray-1000"
            translatedContent={language.eventStudyMetricChangeConfidenceTooltip(
              {
                percentage: confidence.percentage,
                sampleSize: confidence.sampleSize,
                duration,
                metricDefinition,
              },
            )}
          />
        }
        arrow
      >
        <span>
          <Spacing
            direction="horizontal"
            size="gap-1"
            className="border-1 rounded border-[#ECECEC] items-center pl-1 pr-2 py-0.5 w-fit"
          >
            <IconInformationSvg
              wantsColor="#9DA0A3"
              svg={{ className: 'w-[14px] h-[14px]' }}
            />
            <S2Typography
              translatedContent={language.eventStudyMetricConfidence({
                percentage: confidence.percentage,
              })}
              textColorVariant={'gray-600'}
              variant={'body-7'}
              dangerouslyOverrideClassNameFn={(cn) =>
                `${cn.replace('font-normal', '')} font-medium`
              }
            />
          </Spacing>
        </span>
      </LightTooltip>
    </Spacing>
  );
};

export const MetricChangeSection = ({
  beforeJump,
  jump,
  afterJump,
  metricDefinition,
  focusArea,
}: MetricChangeSectionSerializableProps) => {
  const language = useLanguage();
  return (
    <Spacing direction="vertical" size="sm">
      <Spacing direction="horizontal" size="sm">
        <LightTooltip
          title={
            <S2Typography
              as={'span'}
              variant={'body-7'}
              textColorVariant="gray-1000"
              translatedContent={language.eventStudyMetricChangeTooltip()}
            />
          }
          arrow
        >
          <span>
            <IconInformationSvg
              svg={{
                className:
                  'relative w-5 h-5 text-gray-400 hover:text-royal-700',
              }}
            />
          </span>
        </LightTooltip>
        <S2Typography
          translatedContent={language.eventStudyMetricChangeHeading({
            metricDefinition,
          })}
          textColorVariant={'gray-600'}
          variant={'body-7'}
          dangerouslyOverrideClassNameFn={(cn) => `${cn} uppercase`}
        />
      </Spacing>
      <Spacing direction="horizontal" size="lg">
        <MetricChange
          label={language.eventStudyMetricChangeBeforeJumpLabel()}
          emphasized={false}
          metricDefinition={metricDefinition}
          focusArea={focusArea}
          {...beforeJump}
        />
        <MetricChange
          label={language.eventStudyMetricChangeJumpLabel()}
          emphasized={true}
          metricDefinition={metricDefinition}
          focusArea={focusArea}
          {...jump}
        />
        <MetricChange
          label={language.eventStudyMetricChangeAfterJumpLabel()}
          emphasized={false}
          metricDefinition={metricDefinition}
          focusArea={focusArea}
          {...afterJump}
        />
      </Spacing>
    </Spacing>
  );
};
